import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { MapPin, Smartphone, Globe, Monitor, ChevronDown } from 'lucide-react'
import { UAParser } from 'ua-parser-js'

interface UserInfoData {
  ip: string
  country: string
  userAgent: string
  timestamp: number
}

const UserInfo: React.FC = () => {
  const { t } = useTranslation()
  const [userInfo, setUserInfo] = useState<UserInfoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch('/api/userinfo')
        if (!response.ok) {
          throw new Error('Failed to fetch user info')
        }
        const data = await response.json()
        setUserInfo(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchUserInfo()
  }, [])

  if (loading) {
    return (
      <div className="apple-card-solid p-6">
        <h3 className="text-lg font-semibold text-apple-gray-900 mb-6">{t('userInfo')}</h3>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full shimmer" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-20 rounded shimmer" />
                <div className="h-4 w-32 rounded shimmer" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error || !userInfo) {
    return (
      <div className="apple-card-solid p-6">
        <h3 className="text-lg font-semibold text-apple-gray-900 mb-4">{t('userInfo')}</h3>
        <div className="text-apple-red text-sm bg-apple-red/10 p-4 rounded-apple">
          {error || 'Failed to load user information'}
        </div>
      </div>
    )
  }

  // Parse user agent
  const parser = new UAParser(userInfo.userAgent)
  const result = parser.getResult()

  const getCountryName = (countryCode: string) => {
    const countries: { [key: string]: string } = {
      'US': 'United States',
      'CN': 'China',
      'JP': 'Japan',
      'DE': 'Germany',
      'GB': 'United Kingdom',
      'FR': 'France',
      'CA': 'Canada',
      'AU': 'Australia',
      'KR': 'South Korea',
      'IN': 'India',
      'BR': 'Brazil',
      'RU': 'Russia',
      'IT': 'Italy',
      'ES': 'Spain',
      'NL': 'Netherlands',
      'SG': 'Singapore',
      'HK': 'Hong Kong',
      'TW': 'Taiwan',
      'TH': 'Thailand',
      'MY': 'Malaysia'
    }
    return countries[countryCode] || countryCode
  }

  const infoItems = [
    {
      icon: Globe,
      iconBg: 'bg-apple-blue/10',
      iconColor: 'text-apple-blue',
      label: t('ipAddress'),
      value: userInfo.ip,
      mono: true
    },
    {
      icon: MapPin,
      iconBg: 'bg-apple-green/10',
      iconColor: 'text-apple-green',
      label: t('location'),
      value: getCountryName(userInfo.country),
      mono: false
    },
    {
      icon: Smartphone,
      iconBg: 'bg-apple-purple/10',
      iconColor: 'text-apple-purple',
      label: t('device'),
      value: result.device.vendor && result.device.model 
        ? `${result.device.vendor} ${result.device.model}`
        : result.device.type || 'Desktop',
      mono: false
    },
    {
      icon: Monitor,
      iconBg: 'bg-apple-orange/10',
      iconColor: 'text-apple-orange',
      label: t('browser'),
      value: `${result.browser.name} ${result.browser.version}`,
      mono: false
    }
  ]

  return (
    <motion.div 
      className="apple-card-solid p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-semibold text-apple-gray-900 mb-6">{t('userInfo')}</h3>
      
      <div className="space-y-4">
        {infoItems.map((item, index) => (
          <motion.div 
            key={item.label}
            className="flex items-center space-x-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${item.iconBg}`}>
              <item.icon className={`w-5 h-5 ${item.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-apple-gray-500">{item.label}</div>
              <div className={`text-apple-gray-900 truncate ${item.mono ? 'font-mono text-sm' : ''}`}>
                {item.value}
              </div>
            </div>
          </motion.div>
        ))}

        {/* OS Info */}
        <div className="pt-4 border-t border-apple-gray-100">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-apple-gray-500">{t('os')}</div>
              <div className="text-sm text-apple-gray-900">
                {result.os.name} {result.os.version}
              </div>
            </div>
            <div>
              <div className="text-xs text-apple-gray-500">CPU</div>
              <div className="text-sm text-apple-gray-900">
                {result.cpu.architecture || 'Unknown'}
              </div>
            </div>
          </div>
        </div>

        {/* Technical Details - Expandable */}
        <div className="pt-4 border-t border-apple-gray-100">
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between py-2 text-apple-gray-600 hover:text-apple-gray-900 transition-colors"
            whileTap={{ scale: 0.98 }}
          >
            <span className="text-sm font-medium">Technical Details</span>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </motion.button>
          
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  <div>
                    <div className="text-xs text-apple-gray-500 mb-1">User Agent</div>
                    <div className="p-3 bg-apple-gray-50 rounded-apple text-xs font-mono text-apple-gray-600 break-all">
                      {userInfo.userAgent}
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-apple-gray-500">Engine</span>
                    <span className="text-apple-gray-900">{result.engine.name} {result.engine.version}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-apple-gray-500">Timestamp</span>
                    <span className="text-apple-gray-900 font-mono text-xs">
                      {new Date(userInfo.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

export default UserInfo
